import React, { useRef, useState } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import BackupRestDataProvider from '../../../data/BackupRestDataProvider';
import { FaDownload, FaUpload, FaExclamationTriangle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const BackupTab: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [includeUsers, setIncludeUsers] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const provider = React.useMemo(() => new BackupRestDataProvider(), []);

  const handleDownload = async () => {
    setDownloading(true);
    setMessage(null);
    setError(null);
    try {
      const blob = await provider.downloadBackup();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || t('screens:backup.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setMessage(null);
    setError(null);
  };

  const handleRestoreConfirm = async () => {
    if (!selectedFile) return;
    setRestoring(true);
    setMessage(null);
    setError(null);
    try {
      const result = await provider.restoreBackup(selectedFile, includeUsers);
      setMessage(result.message || t('screens:backup.restoreSuccess'));
      setSelectedFile(null);
      setIncludeUsers(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setError(e.message || t('screens:backup.restoreFailed'));
    } finally {
      setRestoring(false);
      setRestoreConfirmOpen(false);
    }
  };

  return (
    <div>
      <SubsectionPanel title={t('screens:backup.backupTitle')}>
        <InfoPanel header="Download a backup">
          Download a ZIP file containing all your tenant data in MongoDB-compatible JSON format.
          Store it safely — you can use it to restore your data at any time.
        </InfoPanel>
        <button
          className="btn btn-primary"
          style={{ margin: '0.5em' }}
          onClick={handleDownload}
          disabled={downloading}
        >
          <FaDownload style={{ marginRight: 6 }} />
          {downloading ? t('screens:backup.preparingDownload') : t('screens:backup.downloadBackup')}
        </button>
      </SubsectionPanel>

      <SubsectionPanel title={t('screens:backup.restoreTitle')}>
        <InfoPanel header="Restore from backup">
          Upload a previously downloaded backup ZIP file to restore your tenant data.
          All existing data will be erased before the backup is imported. This action cannot be undone.
        </InfoPanel>
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
        {message && <div style={{ color: 'green', marginBottom: 12 }}>{message}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', margin: '0.5em' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            className="form-control"
            style={{ maxWidth: 320 }}
          />
          <button
            className="btn btn-warning"
            onClick={() => setRestoreConfirmOpen(true)}
            disabled={!selectedFile || restoring}
          >
            <FaUpload style={{ marginRight: 6 }} />
            {t('screens:backup.restoreBackup')}
          </button>
        </div>

        {/* Include-users option */}
        <div style={{ margin: '0.75em 0.5em 0' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5em', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={includeUsers}
              onChange={e => setIncludeUsers(e.target.checked)}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span>
              <strong>{t('screens:backup.restoreIncludeUsers')}</strong>
              <br />
              <small style={{ color: '#6c757d' }}>{t('screens:backup.restoreIncludeUsersHint')}</small>
            </span>
          </label>
        </div>

        <PopupDialog
          open={restoreConfirmOpen}
          title={t('screens:backup.confirmRestore')}
          onClose={() => setRestoreConfirmOpen(false)}
          actions={
            <>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setRestoreConfirmOpen(false)}
                disabled={restoring}
              >
                {t('common:cancel')}
              </button>
              <button
                className="btn btn-warning"
                onClick={handleRestoreConfirm}
                disabled={restoring}
                style={{ marginLeft: 12 }}
              >
                {restoring ? t('common:restoring') : t('screens:backup.yesRestore')}
              </button>
            </>
          }
        >
          <p dangerouslySetInnerHTML={{ __html: t('screens:backup.confirmRestoreMessage', { filename: selectedFile?.name }) }} />
          {includeUsers && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6em',
                marginTop: '0.75em',
                padding: '0.6em 0.8em',
                background: 'rgba(255, 193, 7, 0.12)',
                border: '1px solid rgba(255, 193, 7, 0.5)',
                borderRadius: 6,
                color: '#856404',
              }}
            >
              <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0, color: '#e6a817' }} />
              <span dangerouslySetInnerHTML={{ __html: t('screens:backup.restoreIncludeUsersWarning') }} />
            </div>
          )}
        </PopupDialog>
      </SubsectionPanel>
    </div>
  );
};

export default BackupTab;
